import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, CardActions, Button, Typography,
  TextField, Grid, InputLabel, MenuItem, FormControl,
  Select, Collapse, CircularProgress, Divider, Paper,
  Avatar, Container, Alert, Snackbar, Chip, Stack,
  IconButton, Tooltip, Tabs, Tab, List, ListItem,
  ListItemText, IconButton as MuiIconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextareaAutosize
} from '@mui/material';
import {
  ContactPhone as ContactPhoneIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Language as WebsiteIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import Swal from 'sweetalert2';
import apiClient from "@/api/client";

const ContactSettings = () => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  
  // Contact form state with type field
  const [contactForm, setContactForm] = useState({
    label: '',
    value: '',
    type: 'phone', // Add type: 'phone', 'email', 'web'
    note: ''
  });

  // Sections configuration
  const sections = [
    { id: 'events', name: 'Events', icon: '📅', color: '#1976d2' },
    { id: 'community_service', name: 'Community Service', icon: '🤝', color: '#2e7d32' },
    { id: 'dana', name: 'Dana', icon: '🍽️', color: '#ed6c02' },
    { id: 'meditate_with_us', name: 'Meditate With Us', icon: '🧘', color: '#9c27b0' },
    { id: 'arama_pooja', name: 'Arama Pooja', icon: '🕯️', color: '#0288d1' },
    { id: 'monastery_development', name: 'Monastery Development', icon: '🏗️', color: '#7b1fa2' },
    { id: 'donations', name: 'Donations', icon: '💰', color: '#c2185b' },
    { id: 'contact_us', name: 'Contact Us', icon: '📞', color: '#f57c00' }
  ];

  // State for storing contact details for each section
  const [sectionContacts, setSectionContacts] = useState({});

  // Fetch existing contact settings
  useEffect(() => {
    fetchContactSettings();
  }, []);

  const fetchContactSettings = async () => {
    setIsLoading(true);
    try {
      // apiClient automatically includes auth token via interceptor
      const response = await apiClient.get('/contact-setting');
      
      // Axios response data is in response.data
      setSectionContacts(response.data.settings || {});
    } catch (error) {
      console.error('Error fetching contact settings:', error);
      
      // If error is 404 (not found), initialize empty structure
      if (error.response?.status === 404) {
        const initialSettings = {};
        sections.forEach(section => {
          initialSettings[section.id] = [];
        });
        setSectionContacts(initialSettings);
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to load contact settings',
          severity: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setContactForm({
        label: contact.label,
        value: contact.value,
        type: contact.type || 'phone', // Load type, default to phone if not set
        note: contact.note || ''
      });
    } else {
      setEditingContact(null);
      setContactForm({
        label: '',
        value: '',
        type: 'phone',
        note: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingContact(null);
    setContactForm({
      label: '',
      value: '',
      type: 'phone',
      note: ''
    });
  };

  const handleContactFormChange = (field) => (event) => {
    setContactForm({
      ...contactForm,
      [field]: event.target.value
    });
  };

  const getIconForLabel = (contact) => {
    // Use type field if available, otherwise detect from label
    if (contact.type === 'email') return <EmailIcon fontSize="small" />;
    if (contact.type === 'web') return <WebsiteIcon fontSize="small" />;
    
    const labelLower = contact.label.toLowerCase();
    if (labelLower.includes('email')) return <EmailIcon fontSize="small" />;
    if (labelLower.includes('whatsapp')) return <WhatsAppIcon fontSize="small" />;
    if (labelLower.includes('website') || labelLower.includes('web')) return <WebsiteIcon fontSize="small" />;
    return <PhoneIcon fontSize="small" />;
  };

  const saveContact = () => {
    // Validation
    if (!contactForm.label.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a label',
        severity: 'error'
      });
      return;
    }
    if (!contactForm.value.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter contact value',
        severity: 'error'
      });
      return;
    }

    const currentSection = sections[activeTab].id;
    const currentContacts = [...(sectionContacts[currentSection] || [])];
    
    if (editingContact) {
      // Update existing contact - find by index or unique identifier
      const index = currentContacts.findIndex(c => 
        c.label === editingContact.label && c.value === editingContact.value
      );
      if (index !== -1) {
        currentContacts[index] = {
          label: contactForm.label,
          value: contactForm.value,
          type: contactForm.type, // Include type field
          note: contactForm.note || null
        };
      }
    } else {
      // Add new contact with type field
      currentContacts.push({
        label: contactForm.label,
        value: contactForm.value,
        type: contactForm.type, // Include type field
        note: contactForm.note || null
      });
    }
    
    setSectionContacts({
      ...sectionContacts,
      [currentSection]: currentContacts
    });
    
    handleCloseDialog();
    setSnackbar({
      open: true,
      message: `Contact ${editingContact ? 'updated' : 'added'} successfully`,
      severity: 'success'
    });
  };

  const deleteContact = (contactToDelete) => {
    Swal.fire({
      title: 'Delete Contact',
      text: `Are you sure you want to delete "${contactToDelete.label}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        const currentSection = sections[activeTab].id;
        const currentContacts = sectionContacts[currentSection] || [];
        const filteredContacts = currentContacts.filter(
          c => !(c.label === contactToDelete.label && c.value === contactToDelete.value)
        );
        
        setSectionContacts({
          ...sectionContacts,
          [currentSection]: filteredContacts
        });
        
        setSnackbar({
          open: true,
          message: 'Contact deleted successfully',
          severity: 'success'
        });
      }
    });
  };

  const saveAllSettings = async () => {
    setIsLoading(true);
    try {
      // apiClient automatically includes auth token via interceptor
      await apiClient.post('/contact-setting', { settings: sectionContacts });
      
      setSnackbar({
        open: true,
        message: 'All contact settings saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving contact settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save settings. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: 'Discard Changes?',
      text: 'You have unsaved changes. Are you sure you want to discard them?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, discard',
      cancelButtonText: 'No, stay'
    }).then((result) => {
      if (result.isConfirmed) {
        fetchContactSettings(); // Reload from server
      }
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const currentContacts = sectionContacts[sections[activeTab]?.id] || [];

  // Helper to get contact type display chip color
  const getTypeChipColor = (type) => {
    switch(type) {
      case 'email': return '#1976d2';
      case 'web': return '#9c27b0';
      default: return '#4caf50';
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#f5f5f5',
      py: 4,
      px: { xs: 2, sm: 3, md: 4 }
    }}>
      <Container maxWidth="lg">
        {/* Header Card */}
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 3, 
            p: 2, 
            bgcolor: 'white',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
              <ContactPhoneIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="600">
                Contact Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage contact details for different sections of the website
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              startIcon={<CancelIcon />}
              disabled={isLoading}
              sx={{ borderRadius: 2 }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={saveAllSettings}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              sx={{ borderRadius: 2 }}
            >
              {isLoading ? 'Saving...' : 'Save All Changes'}
            </Button>
          </Box>
        </Paper>

        {/* Main Settings Card */}
        <Card 
          elevation={0}
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 2 }}
            >
              {sections.map((section, index) => (
                <Tab
                  key={section.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{section.icon}</span>
                      <span>{section.name}</span>
                    </Box>
                  }
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                />
              ))}
            </Tabs>
          </Box>

          <CardContent sx={{ p: 3 }}>
            {/* Section Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  {sections[activeTab]?.name} - Contact Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage contact information for the {sections[activeTab]?.name?.toLowerCase()} section
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{ borderRadius: 2 }}
              >
                Add Contact
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Contacts List */}
            {currentContacts.length === 0 ? (
              <Paper
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: '#fafafa',
                  borderRadius: 2
                }}
              >
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No contacts added yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click the "Add Contact" button to add contact details for this section
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {currentContacts.map((contact, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: 2,
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: `${sections[activeTab].color}20`, color: sections[activeTab].color }}>
                            {getIconForLabel(contact)}
                          </Avatar>
                          <Typography variant="subtitle1" fontWeight="600">
                            {contact.label}
                          </Typography>
                          <Chip 
                            label={contact.type || 'phone'} 
                            size="small" 
                            sx={{ 
                              fontSize: '0.7rem', 
                              height: 24,
                              bgcolor: `${getTypeChipColor(contact.type || 'phone')}20`,
                              color: getTypeChipColor(contact.type || 'phone'),
                              fontWeight: 500
                            }}
                          />
                        </Box>
                        <Box>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenDialog(contact)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => deleteContact(contact)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="primary" sx={{ mb: 1, fontFamily: 'monospace' }}>
                        {contact.value}
                      </Typography>
                      {contact.note && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                          <InfoIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {contact.note}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Contact Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingContact ? 'Edit Contact' : 'Add New Contact'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Contact Label"
                placeholder="e.g., Monastery, Mr Gamini, WhatsApp, Email"
                value={contactForm.label}
                onChange={handleContactFormChange('label')}
                margin="normal"
                required
                helperText="A descriptive label for this contact"
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Contact Type</InputLabel>
                <Select
                  value={contactForm.type}
                  onChange={handleContactFormChange('type')}
                  label="Contact Type"
                >
                  <MenuItem value="phone">Phone Number</MenuItem>
                  <MenuItem value="email">Email Address</MenuItem>
                  <MenuItem value="web">Website URL</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Contact Value"
                placeholder={
                  contactForm.type === 'phone' ? 'e.g., +94 123 456 789' :
                  contactForm.type === 'email' ? 'e.g., info@example.com' :
                  'e.g., https://example.com'
                }
                value={contactForm.value}
                onChange={handleContactFormChange('value')}
                margin="normal"
                required
                helperText={
                  contactForm.type === 'phone' ? 'Enter phone number with country code' :
                  contactForm.type === 'email' ? 'Enter email address' :
                  'Enter full website URL including https://'
                }
              />
              
              <TextField
                fullWidth
                label="Optional Note"
                placeholder="e.g., available after 7pm, emergency only"
                value={contactForm.note}
                onChange={handleContactFormChange('note')}
                margin="normal"
                multiline
                rows={2}
                helperText="Additional information or special instructions"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog} variant="outlined">
              Cancel
            </Button>
            <Button onClick={saveContact} variant="contained">
              {editingContact ? 'Update' : 'Add'} Contact
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ContactSettings;